import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import SearchField from "@entrilia/shared/components/inputs/SearchField.tsx";
import { Box, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { type GridRowId, type GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { searchDimensionDictionary } from "../../../../../../../../../api/biApi.ts";
import type { ConditionDescriptor } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../../../../context/useClientContext.ts";
import { intercomArticleIds } from "@entrilia/shared/utilities/intercomArticles";
import { useBiCommonDataContext } from "../../../../../contexts/BiCommonDataContext.ts";
import { selectLogQueries } from "../../../../../store/selectors/devToolsSelectors.ts";
import { createDevToolsAction } from "../../../../../store/utils/storeUtils.ts";
import { getTextFilterGridColumns } from "../grid/textFilterGridDataProvider.ts";
import {
  textFilterGridSx,
  toConditionValue,
  toRowId,
} from "@entrilia/shared/components/reporting/text-filter-grid/filterGridHelper.tsx";
import ReadMoreArticleLink from "@entrilia/shared/components/reporting/text-filter-grid/ReadMoreArticleLink.tsx";
import TextFilterGrid from "@entrilia/shared/components/reporting/text-filter-grid/TextFilterGrid.tsx";
import {
  MAX_DICTIONARY_SEARCH_LENGTH,
  type TextFilterGridLoadParams,
  type TextFilterGridLoadResult,
} from "@entrilia/shared/components/reporting/text-filter-grid/textFilterGridTypes.ts";
import { getTextFilterGrouping } from "./textFilterGrouping.ts";
import useCachedTextFilterGridLoadRows from "@entrilia/shared/components/reporting/text-filter-grid/useCachedTextFilterGridLoadRows.ts";

interface Props {
  field: ConditionField;
  selectedValues: (string | null)[];
  narrowingFilters: ConditionDescriptor[];
  showOnlySelectedByDefault?: boolean;
  readonly?: boolean;
  onSelectedValuesChange: (values: (string | null)[]) => void;
}
export const TextFilterCondition = ({
  field,
  selectedValues,
  narrowingFilters,
  showOnlySelectedByDefault,
  readonly,
  onSelectedValuesChange,
}: Props) => {
  const { clientCode } = useClientContext();
  const { dataSetId, dimensionNames, measureNames, isMultiBook, books } = useBiCommonDataContext();
  const dispatch = useDispatch();
  const logQueries = useSelector(selectLogQueries);
  const [textFilter, setTextFilter] = useState("");
  const [selectedValuesFilter, setSelectedValuesFilter] = useState<(string | null)[] | undefined>(
    showOnlySelectedByDefault ? selectedValues : undefined
  );
  const [narrowByOtherConditions, setNarrowByOtherConditions] = useState(false);

  const columns = useMemo(() => getTextFilterGridColumns(field.meta), [field.meta]);

  const grouping = useMemo(
    () =>
      getTextFilterGrouping({
        isMultiBook,
        dimensionName: field.meta.name,
        keyFieldName: field.meta.keyFieldName,
        books,
      }),
    [books, field.meta.keyFieldName, field.meta.name, isMultiBook]
  );

  const dictionaryRequest = useMemo(
    () => ({
      dimensionName: field.meta.name,
      searchTermUserInput: textFilter || undefined,
      conditions: narrowByOtherConditions ? narrowingFilters : undefined,
      dimensionNames: narrowByOtherConditions ? dimensionNames : undefined,
      measureNames: narrowByOtherConditions ? measureNames : undefined,
      selectedValuesUserInput: selectedValuesFilter,
      useQueryLogging: logQueries === true,
    }),
    [
      field.meta.name,
      textFilter,
      narrowByOtherConditions,
      narrowingFilters,
      dimensionNames,
      measureNames,
      selectedValuesFilter,
      logQueries,
    ]
  );

  const queryKey = useMemo(
    () => JSON.stringify([clientCode, dataSetId, dictionaryRequest]),
    [clientCode, dataSetId, dictionaryRequest]
  );

  const isPersistentQuery =
    dictionaryRequest.searchTermUserInput === undefined &&
    dictionaryRequest.conditions === undefined &&
    dictionaryRequest.selectedValuesUserInput === undefined &&
    dictionaryRequest.useQueryLogging === false;

  const loadDictionaryRows = useCallback(
    async (params: TextFilterGridLoadParams): Promise<TextFilterGridLoadResult> => {
      const { range, sortModel, allRows } = params;
      const [sortItem] = sortModel;
      const response = await searchDimensionDictionary(dataSetId, {
        ...dictionaryRequest,
        allRows,
        range,
        sort: sortItem ? { fieldId: sortItem.field, ascending: sortItem.sort !== "desc" } : undefined,
      });
      if (!response.success) {
        throw new Error(response.error?.message ?? "Failed to load dictionary values");
      }
      if (dictionaryRequest.useQueryLogging === true && response.data.loggingItems !== undefined) {
        dispatch(createDevToolsAction("update", { loggingItems: response.data.loggingItems }));
      }
      return {
        rows: response.data.items,
        rowCount: response.data.total,
      };
    },
    [dataSetId, dictionaryRequest, dispatch]
  );
  const cachedLoadRows = useCachedTextFilterGridLoadRows({
    loadRows: loadDictionaryRows,
    queryKey,
    cacheScope: [clientCode, dataSetId, field.meta.name],
    persistent: isPersistentQuery,
  });
  const loadRows = logQueries === true ? loadDictionaryRows : cachedLoadRows;

  const initialSorting = useMemo(
    (): GridSortModel => [{ field: field.meta.keyFieldName, sort: "asc" }],
    [field.meta.keyFieldName]
  );

  const selectedRowIds = useMemo(() => selectedValues.map(toRowId), [selectedValues]);

  const handleSelectionChanged = useCallback(
    (rowIds: GridRowId[]) => onSelectedValuesChange(rowIds.map(toConditionValue)),
    [onSelectedValuesChange]
  );

  const handleShowOnlySelectedChange = useCallback(
    (value: boolean) => {
      setSelectedValuesFilter(value ? selectedValues : undefined);
    },
    [selectedValues]
  );

  const showOnlySelected = selectedValuesFilter !== undefined;

  return (
    <Stack spacing={2} sx={{ flex: 1 }}>
      <SearchField onSearch={setTextFilter} debounceTimeMs={500} maxLength={MAX_DICTIONARY_SEARCH_LENGTH} fullWidth />
      <Box sx={{ pl: 0.5, display: "flex" }}>
        <Typography>Show only selected</Typography>
        <HorizontalFill />
        <Switch checked={showOnlySelected} onChange={(_, value) => handleShowOnlySelectedChange(value)} />
      </Box>
      <Box sx={{ pl: 0.5, display: "flex" }}>
        <Tooltip
          disableInteractive={false}
          arrow
          title={
            <Typography>
              Only show values that match the conditions applied above in the list of conditions.
              <br />
              <ReadMoreArticleLink articleId={intercomArticleIds.CONDITION_SETUP_GUIDE}>Read more</ReadMoreArticleLink>
            </Typography>
          }
        >
          <Typography sx={{ textDecoration: "underline dotted", cursor: "help" }}>
            Narrow by other Conditions
          </Typography>
        </Tooltip>
        <HorizontalFill />
        <Switch
          checked={narrowByOtherConditions}
          onChange={(_, value) => setNarrowByOtherConditions(value)}
          disabled={readonly}
        />
      </Box>
      <Box sx={{ mt: -1, overflow: "auto", maxWidth: "100%", flex: 1, display: "flex" }}>
        <TextFilterGrid
          initialSorting={initialSorting}
          columns={columns}
          loadRows={loadRows}
          queryKey={queryKey}
          selectedRowIds={selectedRowIds}
          readonly={readonly}
          onChanged={handleSelectionChanged}
          grouping={grouping}
          sx={textFilterGridSx}
        />
      </Box>
    </Stack>
  );
};

export default TextFilterCondition;
